import React, { useState, useMemo, useEffect, useCallback } from "react";
import { useTranslation } from "react-i18next";
import { v4 as uuid } from "uuid";
import Table from "../../components/ReactTable/Table";
import TextFilter from "../../components/ReactTable/Filters/TextFilter";
import AdminPageLayout from "../../components/FullPageLayout/AdminPageLayout";
import ButtonGroup from "../../components/Buttons/ButtonGroup";
import { Button, Box, TextField, MenuItem, Divider } from "@mui/material";
import {
  packageNames,
  referenceTypes,
  timeRangeOptions
} from "../../constants";
import { useTrainingObjects } from "../../hooks/apiHooks/trainingObjects";
import TrainingHoursCell from "../../components/Table/TrainingHoursCell";
import { useTableData } from "../../components/ReactTable/useTableData";
import AssignmentSelector from "./AssignmentSelector";
import Filter from "./Filter";
import PreviewChangesModal from "./PreviewChangesModal";
import {
  convertDaysToDurationObject,
  convertDurationObjectToDays,
  translateDurationDays
} from "../../utils/datetime";
import {
  useAssignments,
  useBatchCreateAssignments
} from "../../hooks/assignments";
import { map } from "lodash";
import LoadingButton from "../../components/Buttons/LoadingButton";
import { Tooltip } from "../../components/Tooltip";
import ProcessingModal from "../../components/ProcessingModal/ProcessingModal";
import { convertToInteger } from "../../utils/string";
import usePermissions from "../../redux/selectors/usePermissions";
import useControlledRowSelection from "../../components/ReactTable/useControlledRowSelection";

const initialSortBy = [{ id: "name", desc: false }];

const TRAINING_OBJECT_JSONAPI_REQUEST = {
  fields: {
    trainingObject: [
      "name",
      "number",
      "customerAssignments",
      "coursePackages",
      "daysToComplete",
      "repeatInterval",
      "passingScore",
      "duration"
    ],
    coursePackage: ["name"],
    customerAssignments: ["passingScore", "daysToComplete"]
  },
  include: ["customerAssignments", "coursePackages"]
};

const getInitialFilters = () => ({
  assignedToCustomer: true,
  id: null,
  coursePackage: null,
  courseType: null
});

const getTableId = () => uuid();

const TimeDuration = ({
  value,
  unit,
  disabled,
  onUnitChange,
  onValueChange
}) => {
  const { t } = useTranslation();
  const handleBlur = () => {
    if (!value) {
      onValueChange(0);
    }
  };
  return (
    <Box display="flex">
      <Box minWidth={44} maxWidth={48} pr={1}>
        <TextField
          value={value}
          disabled={disabled}
          onChange={ev => onValueChange(ev.target.value)}
          fullWidth
          variant="filled"
          inputProps={{ className: "small" }}
          hiddenLabel
          onBlur={handleBlur}
        />
      </Box>
      <Box minWidth={104}>
        <TextField
          select
          value={unit}
          onChange={ev => onUnitChange(ev.target.value)}
          variant="filled"
          InputProps={{
            disableUnderline: true,
            inputProps: {
              "data-qa": "bulk-assignment-tool-time-duration-unit-field",
              className: "small"
            }
          }}
          fullWidth
          disabled={disabled}
        >
          {timeRangeOptions.map(({ value, label }) => (
            <MenuItem value={value} key={value}>
              {t(`bulkAssignmentTool.durationLabel.${label}`)}
            </MenuItem>
          ))}
        </TextField>
      </Box>
    </Box>
  );
};

const columns = t => [
  {
    Header: t("table.columnHeader.title"),
    accessor: "name",
    Filter: TextFilter,
    filterPlaceholder: t("table.filterPlaceholder.nameAll"),
    id: "name"
  },
  {
    Header: t("table.columnHeader.number"),
    accessor: "number",
    Filter: TextFilter,
    filterPlaceholder: t("table.filterPlaceholder.numberAll"),
    id: "number"
  },
  {
    Header: t("table.columnHeader.coursePackageTitle"),
    accessor: "packageName",
    Filter: TextFilter,
    filterPlaceholder: t("table.filterPlaceholder.nameAll"),
    id: "packageName",
    Cell: ({
      row: {
        original: { coursePackages }
      }
    }) => coursePackages[0]?.name
  },
  {
    Header: t("table.columnHeader.timeToComplete"),
    Cell: ({
      row: {
        original: { id },
        state: { daysToComplete },
        setState
      },
      state: { selectedRowIds }
    }) => (
      <TimeDuration
        onUnitChange={newUnit =>
          setState(({ daysToComplete, ...oldState }) => ({
            ...oldState,
            daysToComplete: { ...daysToComplete, unit: newUnit }
          }))
        }
        onValueChange={newValue =>
          setState(({ daysToComplete, ...oldState }) => ({
            ...oldState,
            daysToComplete: {
              ...daysToComplete,
              value: convertToInteger(newValue)
            }
          }))
        }
        value={daysToComplete.value}
        unit={daysToComplete.unit}
        disabled={!Object.keys(selectedRowIds).find(rowId => rowId === id)}
      />
    ),
    accessor: "timeToComplete",
    id: "days_to_complete"
  },
  {
    Header: t("table.columnHeader.recurrence"),
    Cell: ({
      row: {
        original: { id },
        state: { repeatInterval },
        setState
      },
      state: { selectedRowIds }
    }) => (
      <TimeDuration
        onUnitChange={newUnit =>
          setState(({ repeatInterval, ...oldState }) => ({
            ...oldState,
            repeatInterval: { ...repeatInterval, unit: newUnit }
          }))
        }
        onValueChange={newValue =>
          setState(({ repeatInterval, ...oldState }) => ({
            ...oldState,
            repeatInterval: {
              ...repeatInterval,
              value: convertToInteger(newValue)
            }
          }))
        }
        value={repeatInterval.value}
        unit={repeatInterval.unit}
        disabled={!Object.keys(selectedRowIds).find(rowId => rowId === id)}
      />
    ),
    accessor: "recurrence",
    id: "repeat_interval"
  },
  {
    Header: t("table.columnHeader.passingScore"),
    accessor: "passingScore",
    id: "passingScore"
  },
  {
    Header: t("table.columnHeader.duration"),
    accessor: "duration",
    Cell: ({ cell: { value } }) => <TrainingHoursCell duration={value} />,
    id: "duration"
  }
];

const BulkAssignmentTool = () => {
  const {
    hasPolicyPartnerAdminAccess,
    taxCreditSeriesPartialSubscription
  } = usePermissions();
  const { getTrainingObjects } = useTrainingObjects(
    TRAINING_OBJECT_JSONAPI_REQUEST
  );
  const {
    selectedRows,
    updateControlledSelection,
    resetSelectedRows
  } = useControlledRowSelection();
  const { fetchAssignments, loading: isFetchingAssignments } = useAssignments();
  const {
    createAssignments: saveAssignments,
    isCreating: isSavingAssignments
  } = useBatchCreateAssignments();
  const [filters, setFilters] = useState(getInitialFilters());
  const [assignmentData, setAssignmentData] = useState({
    type: "position",
    items: []
  });

  // Enable the tax credit series rows if the customer does not have a partial subscription
  // or if no items are selected or if only the Tax Credit Series group is selected
  const disableTaxCreditSeriesRows = useMemo(
    () =>
      taxCreditSeriesPartialSubscription &&
      assignmentData.items.length > 0 &&
      (assignmentData.items.length !== 1 ||
        assignmentData.items[0].name !== packageNames.taxCreditSeries),
    [assignmentData, taxCreditSeriesPartialSubscription]
  );

  const [tableId, setTableId] = useState(getTableId());
  const [previewChangesModalOpen, setPreviewChangesModalOpen] = useState(false);
  const [newAssignments, setNewAssignments] = useState([]);
  const { t } = useTranslation();
  const memoColumns = useMemo(
    () =>
      hasPolicyPartnerAdminAccess
        ? columns(t)
        : columns(t).filter(x => x.id !== "number"),
    []
  );
  const {
    data: courses,
    paginationState,
    onStateChange,
    isFetching,
    rowState,
    onApplyExternalFilters
  } = useTableData(getTrainingObjects, {
    externalFilters: filters,
    sortBy: initialSortBy
  });

  const isTaxCredit = course => {
    return !!course.coursePackages.find(
      pkg => pkg.name === "Tax Credit Series"
    );
  };

  useEffect(() => {
    onApplyExternalFilters({
      id: filters.trainingObject?.id || null,
      courseType: filters.courseType,
      coursePackage: filters.coursePackage?.id || null,
      assignedToCustomer: true
    });
  }, [filters]);

  const disableRow = useCallback(
    ({ original: row }) => disableTaxCreditSeriesRows && isTaxCredit(row),
    [disableTaxCreditSeriesRows]
  );

  const getRowTooltipProps = useCallback(
    row =>
      disableRow(row)
        ? {
            title: t("bulkAssignmentTool.taxCreditSeriesTooltip"),
            showTooltipOnMouseEntryPoint: true,
            hideArrow: true,
            interactive: false,
            margin: 0
          }
        : null,
    [disableRow]
  );

  useEffect(() => {
    if (disableTaxCreditSeriesRows && !!selectedRows) {
      const rowIdsToUncheck = Object.keys(selectedRows).reduce(
        (rowIds, rowId) => {
          if (isTaxCredit(rowState[rowId])) {
            rowIds.push(rowId);
          }
          return rowIds;
        },
        []
      );
      updateControlledSelection(rowIdsToUncheck, false);
    }
  }, [disableTaxCreditSeriesRows]);

  const selectedRowList = !!selectedRows && Object.keys(selectedRows);

  const resetTable = () => {
    resetSelectedRows();
    setFilters(getInitialFilters());
    setTableId(getTableId());
    setAssignmentData({ type: "position", items: [] });
  };

  const getAssignmentChanges = (
    newDaysToComplete,
    existingDaysToComplete,
    newRepeatInterval,
    existingRepeatInterval
  ) => {
    let changeNotes = [];
    const numNewDaysToComplete = convertDurationObjectToDays(newDaysToComplete);
    const numNewRepeatIntervalDays = convertDurationObjectToDays(
      newRepeatInterval
    );
    if (numNewDaysToComplete !== existingDaysToComplete) {
      changeNotes.push(
        t("bulkAssignmentTool.daysToCompleteChangedMsg", {
          daysToComplete: translateDurationDays(t, existingDaysToComplete)
        })
      );
    }
    if (numNewRepeatIntervalDays !== existingRepeatInterval) {
      changeNotes.push(
        t("bulkAssignmentTool.repeatIntervalChangedMsg", {
          repeatInterval: translateDurationDays(t, existingRepeatInterval)
        })
      );
    }
    if (!changeNotes.length) {
      changeNotes.push(t("bulkAssignmentTool.noChangesMsg"));
    }
    return changeNotes;
  };

  const previewChanges = async () => {
    const selectedCourses = selectedRowList.reduce(
      (assignments, selectedId) => [...assignments, rowState[selectedId]],
      []
    );

    const previewParams = {
      trainingObjectIdIn: map(selectedCourses, "id"),
      referenceTypeEq: assignmentData.type,
      referenceIdIn: map(assignmentData.items, "id")
    };
    const existingAssignments = await fetchAssignments(previewParams);
    const assignments = selectedCourses.reduce(
      (newAssignmentList, assignmentCourse) => {
        assignmentData.items.forEach(reference => {
          let changeNotes = [];
          // Existing assignment for the position/location/group and the selected training object
          const existingAssignment = existingAssignments.find(
            ({ trainingObject, reference: { id: referenceId } }) =>
              `${trainingObject.id}` === assignmentCourse.id &&
              reference.id === referenceId
          );
          const hasExistingAssignment = !!existingAssignment;
          if (hasExistingAssignment) {
            changeNotes = getAssignmentChanges(
              assignmentCourse.daysToComplete,
              existingAssignment.daysToComplete,
              assignmentCourse.repeatInterval,
              existingAssignment.repeatInterval
            );
          } else {
            changeNotes = [t("bulkAssignmentTool.newAssignmentMsg")];
          }
          // Changes were made to assignment or new assignment was created
          newAssignmentList.push({
            changes: changeNotes,
            referenceName: reference.name,
            courseName: assignmentCourse.name,
            daysToComplete: assignmentCourse.daysToComplete,
            repeatInterval: assignmentCourse.repeatInterval,
            id: `${assignmentCourse.id}-${reference.id}`, // each table row needs a unique ID
            trainingObjectId: assignmentCourse.id,
            referenceId: reference.id,
            referenceType: assignmentData.type,
            existingAssignmentId: existingAssignment?.id
          });
        });
        return newAssignmentList;
      },
      []
    );
    setNewAssignments(assignments);
    setPreviewChangesModalOpen(true);
  };

  const handleSaveAssignments = () => {
    const batchAssignmentData = newAssignments.map(newAssignment => ({
      daysToComplete: convertDurationObjectToDays(newAssignment.daysToComplete),
      repeatInterval: convertDurationObjectToDays(newAssignment.repeatInterval),
      trainingObjectId: newAssignment.trainingObjectId,
      referenceId: newAssignment.referenceId,
      referenceType: referenceTypes[newAssignment.referenceType],
      op: !!newAssignment.existingAssignmentId ? "update" : "add",
      id: newAssignment.existingAssignmentId
    }));
    saveAssignments(batchAssignmentData, true).then(() => {
      resetTable();
      setPreviewChangesModalOpen(false);
    });
  };

  const handleCheckRow = ({ setState, original }, checked) => {
    if (!checked) {
      setState(getInitialRowState(original));
    }
  };

  const getInitialRowState = rowData => {
    const daysToCompleteDuration = convertDaysToDurationObject(
      rowData.daysToComplete
    );
    const repeatIntervalDuration = convertDaysToDurationObject(
      rowData.repeatInterval
    );

    return {
      ...rowData,
      daysToComplete: daysToCompleteDuration,
      repeatInterval: repeatIntervalDuration
    };
  };

  const previewButtonDisabled =
    !selectedRowList.length || !assignmentData.items.length;

  return (
    <AdminPageLayout title={t("bulkAssignmentTool.title")}>
      <AssignmentSelector
        onAssignmentDataChange={setAssignmentData}
        assignmentData={assignmentData}
      />
      <Divider light />
      <Filter onFilterChange={setFilters} filters={filters} />
      <Table
        key={tableId}
        data={courses}
        columns={memoColumns}
        onStateChange={({ selectedRowIds, ...state }) => onStateChange(state)}
        paginationState={paginationState}
        paginated
        loading={isFetching}
        showSelectAllCheckbox
        initialRowStateAccessor={({ original }) => getInitialRowState(original)}
        showRowSelection
        onCheckRow={handleCheckRow}
        initialSortBy={initialSortBy}
        controlledSelectedRows={selectedRows}
        updateControlledSelection={updateControlledSelection}
        rowSelectionDisabled={disableRow}
        getRowTooltipProps={getRowTooltipProps}
      />
      <Box mt={3}>
        <ButtonGroup align="right">
          <Button color="primary" onClick={resetTable}>
            {t("bulkAssignmentTool.resetBtn")}
          </Button>
          <Tooltip
            title={
              previewButtonDisabled
                ? t("bulkAssignmentTool.previewBtnDisabledTooltip", {
                    referenceType: assignmentData.type
                  })
                : ""
            }
          >
            <div>
              <LoadingButton
                color="primary"
                variant="contained"
                onClick={previewChanges}
                disabled={previewButtonDisabled || isFetchingAssignments}
                pending={isFetchingAssignments}
                data-qa="bulk-assignment-tool-preview-btn"
              >
                {t("bulkAssignmentTool.previewBtn")}
              </LoadingButton>
            </div>
          </Tooltip>
        </ButtonGroup>
      </Box>
      <PreviewChangesModal
        open={previewChangesModalOpen}
        assignments={newAssignments}
        onCancel={() => setPreviewChangesModalOpen(false)}
        assignmentType={assignmentData.type}
        onSubmit={handleSaveAssignments}
        isSubmitting={isSavingAssignments}
      />
      <ProcessingModal
        open={isSavingAssignments}
        delay={1000}
        messageText={t("bulkAssignmentTool.processingMsg")}
      />
    </AdminPageLayout>
  );
};

export default BulkAssignmentTool;
